import * as React from "react";
import {
  Box,
  Spinner,
  Flex,
  AvatarProps,
  useMultiStyleConfig,
  BoxProps,
} from "@chakra-ui/react";
import { Avatar } from "shared";
import { useFileUrl } from "hooks";
import { useUserProfilePicture } from "api/queries/useUserProfilePicture";
import { useAuth0 } from "@auth0/auth0-react";

type UserAvatarProps = {
  picture?: string | null;
  currentUserPicture?: boolean;
  boxProps?: BoxProps;
} & AvatarProps;

function UserAvatar(props: UserAvatarProps) {
  const { user } = useAuth0();
  const { currentUserPicture = false, boxProps, ...avatarProps } = props;
  const {
    name,
    title,
    picture = "",
    w,
    h,
    width,
    height,
    size,
    ...rest
  } = avatarProps;
  const normalizedPicture = picture?.trim() ?? "";
  const hasPicture = normalizedPicture.length > 0;

  const nameFromProps = name ?? title;

  // Queries
  const {
    data: file,
    isLoading: isFileLoading,
    isError,
  } = useUserProfilePicture(normalizedPicture, currentUserPicture);

  const fileUrl = useFileUrl(file?.data);

  const auth0FirstName = user?.given_name ?? "";
  const auth0LastName = user?.family_name ?? "";
  const auth0Name = `${auth0FirstName} ${auth0LastName}`.trim();

  // Without currentUserPicture, a blank author name must stay blank: falling
  // back to auth0Name would label someone else's row with the viewer's name.
  const currentUserFallbackName = currentUserPicture ? auth0Name : undefined;
  const displayName = nameFromProps || currentUserFallbackName;

  const avatarSizingProps = { w, h, width, height, size };

  const styles = useMultiStyleConfig("Avatar", { size });
  const widthTemplate =
    w || width || (styles.container.width as string) || "140px";
  const heightTemplate =
    h || height || (styles.container.height as string) || "140px";

  return (
    <Box {...boxProps}>
      {isFileLoading && hasPicture ? (
        <Flex
          width={widthTemplate}
          height={heightTemplate}
          align="center"
          justify="center"
          {...rest}
        >
          <Spinner color="blue" width="40px" height="40px" />
        </Flex>
      ) : isError ? (
        <Avatar
          bg="#9e2700"
          loading="eager"
          name={displayName}
          {...rest}
          {...avatarSizingProps}
        />
      ) : (
        <Avatar
          bg="#9e2700"
          loading="eager"
          src={fileUrl || ""}
          name={displayName}
          {...rest}
          {...avatarSizingProps}
        />
      )}
    </Box>
  );
}

export { UserAvatar };
